/* eslint-disable react/jsx-no-duplicate-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import FormControl from '@material-ui/core/FormControl';
import {
  SelectTypeaheadMultiAtom,
  SelectTypeaheadMultiAtomProps
} from '../atoms/SelectTypeahead/SelectTypeaheadMultiAtom';
import { getSelectedOptions } from '../atoms/SelectTypeahead/parts/getSelectedOptions';
import { TypeaheadOption } from '../atoms/SelectTypeahead/parts/types';
import { FieldValue } from '../types';
import { MatchHintSlot } from './MatchHintSlot';

export interface SelectTypeaheadMultiProps<OptionValue, TargetModel extends any = OptionValue>
  extends Omit<
    SelectTypeaheadMultiAtomProps<OptionValue, TargetModel>,
    'errors' | 'error' | 'touched' | 'onTouched' | 'onSetFieldValue' | 'value'
  > {
  isEqual?: (optionValue: OptionValue, selectedOptionValue: OptionValue) => boolean;
  // opt-in fn which allows "creating" missing dropdown options (component cannot find a dropdown option with that value)
  // e.g. model: { uid: 3 }
  // but you provided dropdown options just for uids: 1,2 ( uid "3" might be deleted or will come later in some case: e.g. pagination)
  // By default value without match is removed on blur event but thanks to that
  // this value is persisted in model and user will show any message you want
  onMissingOption?: (value: OptionValue) => TypeaheadOption<OptionValue>;
  initialValue?: string;
  onSetFieldValue?: SelectTypeaheadMultiAtomProps<OptionValue, TargetModel>['onSetFieldValue'];
  recentItems?: TypeaheadOption<FieldValue>[];
}

export const SelectTypeaheadMulti = <OptionValue extends any = string, TargetModel extends any = OptionValue[]>({
  name,
  required,
  label,
  helpText,
  infoText,
  placeholder,
  fullWidth,
  mapInputValue,
  initialValue,
  isEqual,
  onMissingOption,
  mapOutputValue,
  nullifyNoneOption,
  values,
  onSetFieldValue,
  recentItems,
  ...props
}: SelectTypeaheadMultiProps<OptionValue, TargetModel>) => {
  const [input, meta, helpers] = useField<TargetModel | undefined | null>(name || 'INVALID');
  const formikContext = useFormikContext();
  const [isInitialValueSet, setIsInitialValueSet] = useState(false);
  const valueSetter = useMemo(() => onSetFieldValue ?? helpers.setValue, [onSetFieldValue, helpers.setValue]);
  const onChange = useCallback(
    (model: TargetModel | undefined | null) => {
      valueSetter(model);
    },
    [valueSetter]
  );

  // Get the selected options
  const selectedOptions = useMemo(
    () =>
      getSelectedOptions(
        input.value,
        values || [],
        true,
        isInitialValueSet,
        mapInputValue,
        initialValue,
        isEqual,
        onMissingOption
      ),
    [isInitialValueSet, input.value, values, mapInputValue, initialValue, isEqual, onMissingOption]
  );

  const numValues = values?.length;

  // Set initial value if its specified
  useEffect(() => {
    const canSet = !isInitialValueSet && !!initialValue && !!numValues;
    if (canSet) {
      if (selectedOptions?.length) {
        const targetModel = mapOutputValue
          ? (mapOutputValue(selectedOptions.map((option) => option.value)) as TargetModel)
          : (selectedOptions.map((option) => option.value) as TargetModel);

        valueSetter(targetModel || (nullifyNoneOption ? null : undefined));
      } else {
        valueSetter(nullifyNoneOption ? null : undefined);
      }

      setIsInitialValueSet(true);
    }
  }, [isInitialValueSet, selectedOptions, valueSetter, initialValue, mapOutputValue, nullifyNoneOption, numValues]);

  const hasSubmitted = formikContext.submitCount > 0;

  return (
    <FormControl error={!!meta.error && !!meta.touched} required={required} fullWidth={fullWidth}>
      <MatchHintSlot name={name}>
        <SelectTypeaheadMultiAtom<OptionValue, TargetModel>
          name={name}
          label={label}
          placeholder={placeholder}
          key={name}
          selected={selectedOptions}
          fullWidth={fullWidth}
          helpText={hasSubmitted && meta.error ? meta.error : helpText}
          infoText={infoText}
          required={required}
          error={hasSubmitted && meta.error ? meta.error : ''}
          onSetFieldValue={onChange}
          value={input.value}
          mapInputValue={mapInputValue}
          mapOutputValue={mapOutputValue}
          nullifyNoneOption={nullifyNoneOption}
          values={values}
          recentItems={recentItems}
          {...props}
        />
      </MatchHintSlot>
    </FormControl>
  );
};
